import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { RouteError } from "@/components/RouteError";
import { AssetNotFound } from "@/features/assets/components/AssetScreen";
import { titleOf } from "@/features/assets/media";
import { assetQuery } from "@/features/assets/queries";
import { BasemapEditor } from "@/features/basemaps/components/BasemapEditor";
import { isNotFound } from "@/lib/api";

/**
 * A blank basemap's pixel level, as the create dialog chose it, and where a basemap's editor is:
 * the map's kind, the place at its middle and how far it's zoomed (`lat,lon,zoom`), the tool in
 * hand, and the layer edits go to. The editor keeps these up to date, so a reload comes back to them.
 */
export interface EditSearch {
  level?: number;
  view?: "flat" | "globe";
  at?: string;
  tool?: (typeof TOOLS)[number];
  layer?: string;
}

const TOOLS = [
  "select",
  "elevation",
  "category",
  "color",
  "ice",
  "annotate",
  "resolution",
] as const;
const AT = /^-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?$/;

/** One draft of a basemap, open in the basemap editor, in the room its owner and collaborators share. */
export const Route = createFileRoute("/_authenticated/assets/id/$assetId/drafts/$versionId")({
  staticData: { access: "account" },
  validateSearch: (search: Record<string, unknown>): EditSearch => {
    const level = Number(search.level);
    const at = String(search.at ?? "");
    const layer = String(search.layer ?? "");
    return {
      ...(Number.isInteger(level) && level >= 9 && level <= 11 && { level }),
      ...((search.view === "flat" || search.view === "globe") && { view: search.view }),
      ...(AT.test(at) && { at }),
      ...((TOOLS as readonly unknown[]).includes(search.tool) && {
        tool: search.tool as EditSearch["tool"],
      }),
      ...(/^[a-z][A-Za-z0-9]{0,63}$/.test(layer) && { layer }),
    };
  },
  loader: ({ context: { queryClient, session }, params: { assetId } }) =>
    queryClient.ensureQueryData(assetQuery(assetId, session.user.id)).then(({ asset }) => asset),
  head: ({ loaderData: asset }) => ({
    meta: [{ title: asset ? `Edit ${titleOf(asset)} · Pax.co` : "Edit basemap · Pax.co" }],
  }),
  errorComponent: props =>
    isNotFound(props.error) ? <AssetNotFound /> : <RouteError {...props} />,
  component: DraftPage,
});

function DraftPage() {
  const { assetId, versionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const remember = useCallback(
    (next: Partial<EditSearch>) =>
      void navigate({ search: current => ({ ...current, ...next }), replace: true }),
    [navigate],
  );
  const { session } = Route.useRouteContext();
  const { data: asset } = useSuspenseQuery(assetQuery(assetId, session.user.id));
  if (asset.kind !== "basemap") return <AssetNotFound />;
  return (
    <BasemapEditor
      key={versionId}
      asset={asset}
      versionId={versionId}
      search={search}
      onSearch={remember}
    />
  );
}
